import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import React__default, { useState, useRef, useCallback, useEffect, useLayoutEffect, createContext } from 'react';
import Divider from '../Divider.js';
import { TabsContent } from './TabsContent.js';
import { TabsItem } from './TabsItem.js';
import TabsOverflowDropdown from './TabsOverflowDropdown.js';

var _excluded = ["child", "updateTabDimensions", "hiddenItems", "handleKeyDown", "dropdownIsVisible", "setDropdownIsVisible"],
  _excluded2 = ["size", "justified", "className", "activeTab", "defaultTab", "onTabSelect", "disableAdaptive", "children"];
var TabsContext = /*#__PURE__*/createContext();
var isActive = function isActive(activeItem, items) {
  return activeItem && items && items.filter(function (item) {
    return item.name === activeItem;
  }).length > 0;
};
var MeasuredTab = function MeasuredTab(_ref) {
  var child = _ref.child,
    updateTabDimensions = _ref.updateTabDimensions,
    hiddenItems = _ref.hiddenItems,
    handleKeyDown = _ref.handleKeyDown;
    _ref.dropdownIsVisible;
    var setDropdownIsVisible = _ref.setDropdownIsVisible,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var tabRef = useRef();
  useEffect(function () {
    var currentWidth = tabRef.current.offsetWidth; //individual tab width
    var tabName = child.props.name || null;
    var label = child.props.children;
    updateTabDimensions(tabName, currentWidth, label, child);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTabDimensions, child.props.name]);
  if (child && !hiddenItems[child.props.name]) {
    return /*#__PURE__*/React__default.createElement("li", _extends({
      key: "".concat(child.props.name, "_item"),
      className: "C-Tabs__Item",
      ref: tabRef
    }, otherProps, {
      role: "presentation",
      onClick: function onClick() {
        return setDropdownIsVisible(false);
      }
    }), /*#__PURE__*/React__default.createElement(child.type, _extends({}, child.props, {
      onKeyDown: function onKeyDown(e) {
        return handleKeyDown(e, child.props.name);
      }
    })));
  } else {
    return '';
  }
};
MeasuredTab.displayName = 'MeasuredTab';

// parent renderer
var Tabs = function Tabs(props) {
  var size = props.size,
    justified = props.justified,
    className = props.className,
    activeTab = props.activeTab,
    defaultTab = props.defaultTab,
    _onTabSelect = props.onTabSelect,
    _props$disableAdaptiv = props.disableAdaptive,
    disableAdaptive = _props$disableAdaptiv === void 0 ? false : _props$disableAdaptiv,
    propsChildren = props.children,
    otherProps = _objectWithoutProperties(props, _excluded2);
  // Return an array of children
  var children = React__default.Children.toArray(propsChildren);
  var finalClasses = classnames('C-Tabs', _defineProperty(_defineProperty({
    '--disable-adaptive': disableAdaptive
  }, "--size-".concat(size), size), '--space-between', justified), className);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    tabCache = _useState2[0],
    setTabCache = _useState2[1];
  var _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    tabRowDimensions = _useState4[0],
    setTabRowDimensions = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    tabDimensions = _useState6[0],
    setTabDimensions = _useState6[1];
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    dropdownWidth = _useState8[0],
    setDropdownWidth = _useState8[1];
  var _useState9 = useState({}),
    _useState10 = _slicedToArray(_useState9, 2),
    hiddenItems = _useState10[0],
    setHiddenItems = _useState10[1];
  var tabsContainerRef = useRef();
  var dropdownButtonRef = useRef();
  var updateTabDimensions = useCallback(function (tabName, tabWidth, tabLabel, child) {
    var localTabDimensions = tabDimensions;
    localTabDimensions.push({
      active: child.props.active ? true : false,
      label: tabLabel,
      name: tabName,
      otherProps: child ? child.props : [],
      width: tabWidth
    });
    setTabDimensions(localTabDimensions);
  }, [tabDimensions]);
  useEffect(function () {
    // Create a list of tab names
    var tempArr = [];
    children.map(function (child) {
      if (child) {
        var tabName = child.props.name || null;
        tempArr = tabCache;
        if (!tabCache.includes(tabName)) tempArr.push(tabName);
      }
    });
    setTabCache(tempArr);
  }, [propsChildren]);
  useEffect(function () {
    if (Object.keys(hiddenItems).length) {
      setDropdownWidth(182);
    } else {
      setDropdownWidth(0);
    }
  }, [defaultTab, activeTab, hiddenItems]);
  useLayoutEffect(function () {
    if (!disableAdaptive) {
      var localHiddenItems = {};
      var consumedTabWidth = 0;
      var currentMaxWidth = Math.floor(tabRowDimensions ? tabRowDimensions.width : 0);
      tabDimensions.forEach(function (tab, index) {
        consumedTabWidth += tab.width;
        if (consumedTabWidth + dropdownWidth + 2 > currentMaxWidth) {
          localHiddenItems[tab.name] = {
            active: tab.active,
            index: index,
            label: tab.label,
            name: tab.name,
            otherProps: tab.otherProps
          };
        }
      });
      setHiddenItems(localHiddenItems);
    }
  }, [tabDimensions, tabRowDimensions, dropdownWidth, disableAdaptive]);

  // this will only run if there is a ref, and we only add a ref if !disableAdaptive
  useLayoutEffect(function () {
    if (tabsContainerRef.current) {
      var handleResize = function handleResize() {
        setTabRowDimensions({
          width: tabsContainerRef.current.offsetWidth
        });
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return function () {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [tabsContainerRef]);
  var handleKeyDown = function handleKeyDown(event, name, isOnDropdown) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      var _document$getElementB;
      var currentTabIndex = tabCache.indexOf(name);
      if (currentTabIndex === -1 && !isOnDropdown) {
        console.error('Hey Canvas User! The Tabs.Item component must have a name in order to be selected. Please add name="string" to each tab.');
        return false;
      }
      var tabName = null;

      // Move Right
      if (event.key === 'ArrowRight') {
        if (tabCache.length === currentTabIndex + 1 || isOnDropdown) {
          // We're at the end of the tab list, go to the beginning
          tabName = tabCache[0];
        } else {
          // Advance to the next tab to the "right"
          tabName = tabCache[currentTabIndex + 1];
          if (tabName && hiddenItems[tabName]) {
            dropdownButtonRef.current.focus();
          }
        }

        // Move left
      } else if (event.key === 'ArrowLeft') {
        if (currentTabIndex === 0) {
          // If we're at the beginning of the list, go to the end
          tabName = tabCache[tabCache.length - 1];
          if (tabName && hiddenItems[tabName]) {
            // If the end is a dropdown:
            dropdownButtonRef.current.focus();
          }
        } else {
          // Advance to the next tab to the "left"
          if (isOnDropdown) {
            // go left to the first tab not hidden in the dropdown
            var notHiddenTabs = tabCache.filter(function (n) {
              return !hiddenItems[n];
            });
            tabName = notHiddenTabs[notHiddenTabs.length - 1];
          } else {
            tabName = tabCache[currentTabIndex - 1];
          }
        }
      }

      // focus on left/right tab
      (_document$getElementB = document.getElementById("".concat(tabName, "-tab-control"))) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.focus();
    }
  };
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    dropdownIsVisible = _useState12[0],
    setDropdownIsVisible = _useState12[1];
  var handleUserKeyPress = function handleUserKeyPress(event) {
    var key = event.key,
      keyCode = event.keyCode;
    if (key === 'Escape' || key === 'Esc' || keyCode === 72) {
      dropdownButtonRef.current.focus();
      setDropdownIsVisible(false);
    }
  };

  // listen for escape key press when dropdown menu is visible
  useEffect(function () {
    if (dropdownIsVisible) {
      window.addEventListener('keyup', handleUserKeyPress);
    }
    return function () {
      // returned function will be called on component unmount
      window.removeEventListener('keyup', handleUserKeyPress);
    };
  }, [dropdownIsVisible]);
  return /*#__PURE__*/React__default.createElement(TabsContext.Provider, {
    value: {
      activeTab: activeTab || defaultTab,
      onTabSelect: function onTabSelect(name) {
        return function (event) {
          return _onTabSelect && _onTabSelect(event, {
            name: name
          });
        };
      }
    }
  }, !disableAdaptive ? /*#__PURE__*/React__default.createElement("ul", _extends({
    className: finalClasses
  }, otherProps, {
    role: "tablist",
    ref: tabsContainerRef
  }), children && children.map(function (child) {
    if (child.type.displayName === 'Tabs.Item') {
      return /*#__PURE__*/React__default.createElement(MeasuredTab, {
        child: child,
        key: "".concat(child.props.name, "_measured"),
        updateTabDimensions: updateTabDimensions,
        hiddenItems: hiddenItems,
        handleKeyDown: handleKeyDown,
        dropdownIsVisible: dropdownIsVisible,
        setDropdownIsVisible: setDropdownIsVisible
      });
    }
  }), Object.keys(hiddenItems).length > 0 && /*#__PURE__*/React__default.createElement("li", {
    role: "tablist",
    key: "overflowDropdown",
    tabIndex: 0,
    onKeyDown: function onKeyDown(e) {
      return handleKeyDown(e, null, true);
    }
  }, /*#__PURE__*/React__default.createElement(TabsOverflowDropdown, {
    items: hiddenItems,
    activeItem: activeTab || defaultTab,
    onSelect: _onTabSelect,
    isActive: isActive,
    isVisible: dropdownIsVisible,
    setIsVisible: setDropdownIsVisible,
    dropdownButtonRef: dropdownButtonRef
  }))) : /*#__PURE__*/React__default.createElement("ul", _extends({
    className: finalClasses
  }, otherProps, {
    role: "tablist"
  }), children && children.map(function (child) {
    if (child.type.displayName === 'Tabs.Item') {
      return /*#__PURE__*/React__default.createElement("li", {
        key: "".concat(child.props.name, "_item"),
        role: "presentation",
        className: "C-Tabs__Item"
      }, /*#__PURE__*/React__default.createElement(child.type, _extends({}, child.props, {
        onKeyDown: function onKeyDown(e) {
          return handleKeyDown(e, child.props.name);
        }
      })));
    }
  })), /*#__PURE__*/React__default.createElement(Divider, null), /*#__PURE__*/React__default.createElement("div", {
    "aria-live": "polite"
  }, children.map(function (child, i) {
    if (child && child.type.displayName === 'Tabs.Content') {
      return /*#__PURE__*/React__default.createElement(child.type, _extends({}, child.props, {
        key: i
      }));
    }
  })));
};
Tabs.displayName = 'Tabs';
Tabs.Item = TabsItem;
Tabs.Content = TabsContent;

export { TabsContext, Tabs as default };
